import { useCallback, useEffect, useRef } from 'react';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';

const STORAGE_KEY_PREFIX = 'performance.selectedCountries.';

interface CountryRef {
  country_code: string;
  country_name: string;
}

const readFromUrl = (): string[] | null => {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('countries');
    if (!raw) return null;
    const codes = raw.split(',').map((c) => c.trim().toUpperCase()).filter(Boolean);
    return codes.length > 0 ? codes : null;
  } catch {
    return null;
  }
};

const readFromStorage = (workspaceId: string | number): string[] | null => {
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY_PREFIX}${workspaceId}`);
    if (!raw) return null;
    const codes = JSON.parse(raw);
    return Array.isArray(codes) && codes.length > 0 ? codes : null;
  } catch {
    return null;
  }
};

const writeToStorage = (workspaceId: string | number, codes: string[]) => {
  try {
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${workspaceId}`, JSON.stringify(codes));
  } catch {
    /* ignore storage errors (e.g. quota, privacy mode) */
  }
};

/**
 * Restores the multi-country selection for Explore/Funnel from `?countries=FR,DM`
 * (wins) or localStorage, once on first mount after `countryList` populates. Falls
 * back to every country ("All") when neither source has a value, or when the
 * persisted codes no longer match any country in `countryList`.
 */
export const usePersistedCountries = (
  countryList: CountryRef[],
  onRestore: (names: string[]) => void
) => {
  const workspace: any = useCurrentWorkspace();
  const restoredRef = useRef(false);

  useEffect(() => {
    if (restoredRef.current) return;
    if (!workspace?.id || countryList.length === 0) return;

    const codeToName = new Map(countryList.map((c) => [c.country_code.toUpperCase(), c.country_name]));
    const allNames = countryList.map((c) => c.country_name);

    const codes = readFromUrl() ?? readFromStorage(workspace.id);
    const names = codes
      ? codes.map((code) => codeToName.get(code.toUpperCase())).filter((n): n is string => !!n)
      : null;

    restoredRef.current = true;
    onRestore(names && names.length > 0 ? names : allNames);
  }, [countryList, workspace?.id, onRestore]);

  const persist = useCallback(
    (names: string[]) => {
      if (!workspace?.id) return;
      const codeByName = new Map(countryList.map((c) => [c.country_name, c.country_code]));
      const codes = names.map((n) => codeByName.get(n)).filter((c): c is string => !!c);
      writeToStorage(workspace.id, codes);
    },
    [countryList, workspace?.id]
  );

  return { persist };
};
